import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

export default function AdminReports() {
  const [stats, setStats] = useState(null);
  const [payments, setPayments] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('membership');

  useEffect(() => {
    Promise.all([
      api.get('/admin/dashboard/'),
      api.get('/admin/payments/'),
      api.get('/accounts/admin/members/'),
    ]).then(([statsRes, payRes, memRes]) => {
      setStats(statsRes.data);
      setPayments(payRes.data.results || payRes.data);
      setMembers(memRes.data.results || memRes.data);
    }).catch(() => toast.error('Failed to load report data.'))
      .finally(() => setLoading(false));
  }, []);

  const exportPDF = (title, headers, rows) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`NIS FCT Branch - ${title}`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 28);
    autoTable(doc, { head: [headers], body: rows, startY: 35, theme: 'grid',
      headStyles: { fillColor: [26, 86, 50] } });
    doc.save(`${title.replace(/\s+/g, '_').toLowerCase()}.pdf`);
  };

  const exportExcel = (title, headers, rows) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title);
    XLSX.writeFile(wb, `${title.replace(/\s+/g, '_').toLowerCase()}.xlsx`);
  };

  const getMembershipData = () => {
    const headers = ['Name', 'Email', 'Membership No.', 'Category', 'Status', 'Approved'];
    const rows = members.map(m => [
      `${m.user?.first_name || ''} ${m.user?.last_name || ''}`.trim() || 'N/A',
      m.user?.email || 'N/A', m.membership_number || 'N/A',
      m.category || 'N/A', m.status || 'N/A', m.user?.is_approved ? 'Yes' : 'No',
    ]);
    return { headers, rows };
  };

  const getFinancialData = () => {
    const headers = ['Member', 'Type', 'Amount (₦)', 'Year', 'Status', 'Reference', 'Date'];
    const rows = payments.map(p => [
      p.member_name || 'N/A', p.payment_type || 'N/A',
      parseFloat(p.amount || 0).toLocaleString(), p.year || 'N/A',
      p.status || 'N/A', p.reference || 'N/A',
      p.created_at ? new Date(p.created_at).toLocaleDateString() : 'N/A',
    ]);
    return { headers, rows };
  };

  const membersByCategory = () => {
    const cats = {};
    members.forEach(m => { const c = m.category || 'Unknown'; cats[c] = (cats[c] || 0) + 1; });
    return cats;
  };

  const paymentsByStatus = () => {
    const st = {};
    payments.forEach(p => { const s = p.status || 'unknown'; st[s] = (st[s] || 0) + 1; });
    return st;
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0B5D1E]"></div>
    </div>
  );

  const catData = membersByCategory();
  const payStatusData = paymentsByStatus();

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Reports & Analytics</h1>

      {/* Report Type Tabs */}
      <div className="flex space-x-2 mb-6">
        {['membership', 'financial'].map(t => (
          <button key={t} onClick={() => setReportType(t)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
              reportType === t ? 'bg-[#0B5D1E] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>{t === 'membership' ? 'Membership Report' : 'Financial Report'}</button>
        ))}
      </div>

      {reportType === 'membership' ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Members', value: stats?.total_members || members.length },
              { label: 'Approved', value: stats?.approved_members ?? members.filter(m => m.user?.is_approved).length },
              { label: 'Pending', value: stats?.pending_approvals ?? members.filter(m => !m.user?.is_approved).length },
              { label: 'Categories', value: Object.keys(catData).length },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border p-5 text-center">
                <p className="text-2xl font-bold text-[#0B5D1E]">{c.value}</p>
                <p className="text-sm text-gray-500 mt-1">{c.label}</p>
              </div>
            ))}
          </div>

          {/* Members by Category Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Members by Category</h3>
              <Bar data={{
                labels: Object.keys(catData),
                datasets: [{ label: 'Members', data: Object.values(catData),
                  backgroundColor: '#0B5D1E' }],
              }} options={{ responsive: true, plugins: { legend: { display: false } } }} />
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Category Distribution</h3>
              <Doughnut data={{
                labels: Object.keys(catData),
                datasets: [{ data: Object.values(catData),
                  backgroundColor: ['#0B5D1E', '#1a8a3e', '#45b77a', '#7dd4a1', '#b5ebc9', '#d4f5e0'] }],
              }} />
            </div>
          </div>

          {/* Export */}
          <div className="flex space-x-3">
            <button onClick={() => { const d = getMembershipData(); exportPDF('Membership Report', d.headers, d.rows); }}
              className="bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-red-700">Export PDF</button>
            <button onClick={() => { const d = getMembershipData(); exportExcel('Membership Report', d.headers, d.rows); }}
              className="bg-green-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-green-700">Export Excel</button>
          </div>
        </>
      ) : (
        <>
          {/* Financial Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Payments', value: payments.length },
              { label: 'Verified', value: payments.filter(p => p.status === 'verified').length },
              { label: 'Pending', value: payments.filter(p => p.status === 'pending').length },
              { label: 'Total Amount', value: '₦' + payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0).toLocaleString() },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border p-5 text-center">
                <p className="text-2xl font-bold text-[#0B5D1E]">{c.value}</p>
                <p className="text-sm text-gray-500 mt-1">{c.label}</p>
              </div>
            ))}
          </div>

          {/* Payment Status Chart */}
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Payments by Status</h3>
            <div className="max-w-xs mx-auto">
              <Doughnut data={{
                labels: Object.keys(payStatusData),
                datasets: [{ data: Object.values(payStatusData),
                  backgroundColor: ['#0B5D1E', '#f59e0b', '#ef4444', '#6b7280'] }],
              }} />
            </div>
          </div>

          {/* Export */}
          <div className="flex space-x-3">
            <button onClick={() => { const d = getFinancialData(); exportPDF('Financial Report', d.headers, d.rows); }}
              className="bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-red-700">Export PDF</button>
            <button onClick={() => { const d = getFinancialData(); exportExcel('Financial Report', d.headers, d.rows); }}
              className="bg-green-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-green-700">Export Excel</button>
          </div>
        </>
      )}
    </div>
  );
}
